import type { ExtractedEvent } from '../types';
import { isStaleRecurringProjection } from '../lib/eventDates';

interface Props {
  event: Pick<
    ExtractedEvent,
    | 'isRecurringInstance'
    | 'recurringIsPermanent'
    | 'recurringPattern'
    | 'startUtc'
    | 'postCreatedAt'
  >;
  /** Bootstrap badge size class — `''` for default, `' small'` for card chips. */
  size?: '' | ' small';
  /** Extra class names appended to the badge. */
  className?: string;
}

/**
 * Small badge shown on auto-extracted recurring occurrences whose source
 * post is materially older than the occurrence date — i.e. they were rolled
 * forward by the recurrence expansion and have not been refreshed by a more
 * recent post about that specific date.
 *
 * Renders nothing when the event is fresh, manually permanent, or one-time.
 */
export function StaleRecurringChip({ event, size = '', className = '' }: Props) {
  if (!isStaleRecurringProjection(event)) return null;
  const posted = event.postCreatedAt
    ? new Date(event.postCreatedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : 'unknown';
  return (
    <span
      className={`badge text-bg-light border text-muted${size}${className ? ` ${className}` : ''}`}
      title={`Projected from a recurring series — last related post was ${posted}. No newer post has confirmed this specific date.`}
      aria-label="Projected occurrence — not refreshed by a recent post"
    >
      <span aria-hidden="true">📭</span> Not refreshed
    </span>
  );
}
